import { z } from "zod";
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { useXFlowPartnerContext } from "../../../context/XFlowPartners";
import Dropdown from "../../DropDown";
import {
  PURPOSE_CODES,
  xFlowInvoiceTransactionTypes,
} from "../../../constants/xFlow";
import InputWithLabel from "../../InputWithLabel";
import DragAndDrop from "../../DragAndDrop";
import { fileToBase64 } from "../../../utils";
import {
  add_xflow_receivables,
  common_file_upload,
  serve_file,
} from "../../../constants/apis";
import { useAppContext } from "../../../context/AppContext";
import { toast } from "react-toastify";
import { useEffect, useRef, useState } from "react";
import { DocumentIcon, SpinnerIcon } from "../../Icons";
import Modal from "../../Modal";
import { useXFlowReceivablesContext } from "../../../context/XFlowReceivables";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import AddPartnerForm from "../AddPartnerForm";

export const receivableFormSchema = z.object({
  partner_id: z
    .string({ message: "Partner is required" })
    .trim()
    .min(1, "Partner is required"),
  amount_maximum_reconcilable: z
    .string()
    .trim()
    .min(1, "Receivable Amount is required"),
  currency: z.string().trim().min(1, "Currency is required"),
  invoice_amount: z.string().trim().min(1, "Invoice amount is required"),
  invoice_creation_date: z
    .string()
    .trim()
    .min(1, "Invoice creation date is required"),
  invoice_currency: z.string().trim().min(1, "Invoice currency is required"),
  invoice_document: z.string().trim().min(1, "Invoice document is required"),
  invoice_due_date: z.string().trim().min(1, "Invoice due date is required"),
  invoice_reference_number: z
    .string()
    .trim()
    .min(1, "Invoice reference number is required"),
  purpose_code: z.string().trim().min(1, "Purpose code is required"),
  transaction_type: z.string().trim().min(1, "Transaction type is required"),
});

export type ReceivableFormType = z.infer<typeof receivableFormSchema>;

export default function CreateReceivableForm({
  toggleReceivableModal,
  partnerId,
}: {
  toggleReceivableModal?: () => void;
  partnerId?: string;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const partner_id = searchParams.get("partner_id");
  const isReconcilePage = location.pathname === "/simple-payment/partner";

  const { data: allPartners, refetch } = useXFlowPartnerContext();
  const { refetch: refetchReceivables } = useXFlowReceivablesContext();
  const { jwtToken } = useAppContext();

  // dialog ref
  const addPartnerDialogRef = useRef<HTMLDialogElement>(null);

  const toggleAddPartnerModal = () => {
    if (addPartnerDialogRef.current?.open) {
      addPartnerDialogRef.current?.close();
    } else {
      addPartnerDialogRef.current?.showModal();
    }
  };

  const methods = useForm<ReceivableFormType>({
    resolver: zodResolver(receivableFormSchema),
    defaultValues: partner_id ? { partner_id } : {},
  });

  const {
    register,
    formState: { errors, isSubmitting },
    handleSubmit,
    reset,
    watch,
  } = methods;

  const partnerIdOnChange = watch("partner_id");

  useEffect(() => {
    if (partnerIdOnChange) {
      setSearchParams({ partner_id: partnerIdOnChange });
    } else {
      setSearchParams();
    }
  }, [partnerIdOnChange]);

  //   console.log(errors);

  const submitHandler: SubmitHandler<ReceivableFormType> = async (values) => {
    // console.log(values);

    try {
      const res = await fetch(add_xflow_receivables, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          Content_Type: "application/json",
        },
        body: JSON.stringify(values),
      });
      const data = await res.json();

      if (data.status === "success") {
        if (data?.xflow_response?.http_status_code === 400) {
          toast.error("Receivable creation failed");
        }

        toast.success("Receivable successfully created");
        reset();
        if (isReconcilePage) {
          //   refetch();
          refetchReceivables();
          toggleReceivableModal?.();
        } else {
          refetchReceivables();
          navigate(`/simple-payment/partner?partner_id=${values.partner_id}`);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <section>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(submitHandler)} className="space-y-3">
          {!isReconcilePage && (
            <h3 className="text-lg font-medium">Create Receivable</h3>
          )}
          <div className="flex items-center gap-4">
            <Dropdown
              label="Select Partner"
              placeholder="Select a partner"
              options={
                allPartners
                  ?.filter((el) => el.status === "activated")
                  .map((el) => ({
                    label: el.nickname,
                    value: String(el.id),
                  })) || []
              }
              {...register("partner_id")}
              error={errors.partner_id?.message}
            />

            {!isReconcilePage && (
              <div className="w-[400px] pt-2">
                <button
                  type="button"
                  className="w-full h-[44px] px-2 text-custom-blue-medium font-medium border border-custom-blue-medium rounded-lg disabled:border-gray-600 disabled:text-gray-600 disabled:cursor-not-allowed"
                  onClick={toggleAddPartnerModal}
                >
                  Add new or edit partner
                </button>
              </div>
            )}
          </div>

          <hr />

          <h3 className="text-lg font-medium">Invoice Details</h3>

          <div className="flex gap-3">
            <Dropdown
              label="Type of Transaction"
              placeholder="Select type"
              options={xFlowInvoiceTransactionTypes}
              {...register("transaction_type")}
              error={errors.transaction_type?.message}
            />

            <Dropdown
              label="Invoice Purpose Code"
              placeholder="Select a purpose code"
              options={PURPOSE_CODES}
              {...register("purpose_code")}
              error={errors.purpose_code?.message}
            />

            <InputWithLabel
              label="Invoice Number"
              placeholder="Enter here"
              {...register("invoice_reference_number")}
              error={errors.invoice_reference_number?.message}
            />
          </div>

          <div className="flex gap-3 items-center">
            <div className="w-[500px]">
              <UploadDocument />
            </div>

            <div className="w-full space-y-3">
              <div>
                <div className="flex items-end">
                  <label className="w-full">
                    <span className="block text-[#344054] text-sm font-medium">
                      Invoice Amount
                    </span>
                    <input
                      className="w-full p-[10px] border-[0.66px] border-[#d0d5dd] border-r-0 rounded-l-[8px] placeholder:text-[#344054] focus:border-[#d0d5dd] focus:outline-none focus:ring-2 focus:ring-[rgba(21,93,233,0.2)]"
                      placeholder="Enter here"
                      {...register("invoice_amount")}
                    />
                  </label>
                  <select
                    className="p-[10px] border-[0.66px] border-[#d0d5dd] rounded-r-[8px] placeholder:text-[#344054] focus:border-[#d0d5dd] focus:outline-none focus:ring-2 focus:ring-[rgba(21,93,233,0.2)]"
                    {...register("invoice_currency")}
                  >
                    <option value="USD">USD</option>
                  </select>
                </div>

                <small className="text-red-500 h-[10px] block ml-1">
                  {errors.invoice_amount && errors.invoice_amount?.message}
                </small>
              </div>

              <div>
                <div className="flex items-end">
                  <label className="w-full">
                    <span className="block text-[#344054] text-sm font-medium">
                      Receivable Amount
                    </span>
                    <input
                      placeholder="Enter here"
                      className="w-full p-[10px] border-[0.66px] border-[#d0d5dd] border-r-0 rounded-l-[8px] placeholder:text-[#344054] focus:border-[#d0d5dd] focus:outline-none focus:ring-2 focus:ring-[rgba(21,93,233,0.2)]"
                      {...register("amount_maximum_reconcilable")}
                    />
                  </label>
                  <select
                    className="p-[10px] border-[0.66px] border-[#d0d5dd] rounded-r-[8px] focus:border-[#d0d5dd] focus:outline-none focus:ring-2 focus:ring-[rgba(21,93,233,0.2)]"
                    {...register("currency")}
                  >
                    <option value="USD">USD</option>
                  </select>
                </div>

                <small className="text-red-500 h-[10px] block ml-1">
                  {errors.amount_maximum_reconcilable &&
                    errors.amount_maximum_reconcilable?.message}
                </small>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <InputWithLabel
              label="Invoice Creation Date"
              type="date"
              {...register("invoice_creation_date")}
              error={errors.invoice_creation_date?.message}
            />
            <InputWithLabel
              label="Invoice Due Date"
              type="date"
              {...register("invoice_due_date")}
              error={errors.invoice_due_date?.message}
            />
          </div>

          <div
            className={`mt-4 pt-6 flex ${
              isReconcilePage ? "justify-end" : "justify-between"
            } border-t border-[#EAECF0]`}
          >
            {!isReconcilePage && (
              <button
                type="button"
                disabled={isSubmitting}
                className="w-[87px] h-[44px] border border-[#D0D5DD] text-[#344054] font-medium rounded-lg"
                onClick={() => {
                  navigate("/simple-payment");
                }}
              >
                Back
              </button>
            )}

            <div className="space-x-4">
              {!isReconcilePage && (
                <button
                  type="button"
                  className="h-[44px] px-2 text-custom-blue-medium font-medium border border-custom-blue-medium rounded-lg disabled:border-gray-600 disabled:text-gray-600 disabled:cursor-not-allowed"
                  // onClick={onClickSaveAsDraft}
                  //   disabled={!someDataPresent}
                  disabled
                >
                  {false ? (
                    <p className="space-x-2">
                      <SpinnerIcon className="size-4 text-gray-200 animate-spin fill-blue-600 inline-flex justify-center" />
                      <span>Please wait...</span>
                    </p>
                  ) : (
                    "Save as Draft"
                  )}
                </button>
              )}

              <button
                type="submit"
                className="w-[146px] h-[44px] rounded-md bg-custom-blue-medium text-white font-medium"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <p className="space-x-2">
                    <SpinnerIcon className="size-4 text-gray-200 animate-spin fill-blue-600 inline-flex justify-center" />
                    <span>Please wait...</span>
                  </p>
                ) : isReconcilePage ? (
                  "Save"
                ) : (
                  "Save and Next"
                )}
              </button>
            </div>
          </div>
        </form>
      </FormProvider>

      {!isReconcilePage && (
        <Modal
          ref={addPartnerDialogRef}
          toggleDialog={toggleAddPartnerModal}
          className="p-4 rounded-md backdrop:bg-black/50"
        >
          <AddPartnerForm toggleAddPartnerModal={toggleAddPartnerModal} />
        </Modal>
      )}
    </section>
  );
}

function UploadDocument() {
  const { jwtToken } = useAppContext();
  const viewDocumentDialogRef = useRef<HTMLDialogElement>(null);
  const [documentFileData, setDocumentFileData] = useState({
    fileUrl: "",
    mimeType: "",
    loading: false,
    error: "",
  });

  const toggleDialog = () => {
    if (viewDocumentDialogRef.current?.hasAttribute("open")) {
      viewDocumentDialogRef.current?.close();
      const imgEl = document.getElementById(`blob-data`) as HTMLImageElement;
      imgEl.src = "";
    } else {
      viewDocumentDialogRef.current?.showModal();
    }
  };

  async function getBlobFileData(url: string, jwtToken: string) {
    setDocumentFileData((prev) => ({ ...prev, loading: true }));

    try {
      const res = await fetch(`${serve_file}?file=${url}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwtToken}`,
        },
      });

      const blob = await res.blob();

      const newUrl = URL.createObjectURL(blob);
      setDocumentFileData((prev) => ({
        ...prev,
        fileUrl: newUrl,
        mimeType: blob.type,
      }));
    } catch (error) {
      console.log(error);
      setDocumentFileData({
        fileUrl: "",
        mimeType: "",
        loading: false,
        error: "Something went wrong please try again!",
      });
    } finally {
      setDocumentFileData((prev) => ({ ...prev, loading: false }));
    }
  }

  const {
    clearErrors,
    setValue,
    formState: { errors },
    getValues,
  } = useFormContext<ReceivableFormType>();

  const uploadFile = async (file: File) => {
    try {
      const base64String = await fileToBase64(file);

      const res = await fetch(common_file_upload, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          Content_Type: "application/json",
        },
        body: JSON.stringify({
          base64File: base64String,
          folder_name: "user",
        }),
      });

      const data = await res.json();

      if (data.status === "success") {
        setValue(`invoice_document`, data.url as string);

        clearErrors(`invoice_document`);
        // toast.success(data.msg);
      } else {
        // toast.error("File upload went wrong please try again");
      }
    } catch (error) {
      console.error("Error converting file to Base64:", error);
      // toast.error("File upload went wrong please try again");
    }
  };

  const handleFileChangeAndUpload = async (
    e: React.FormEvent<HTMLInputElement>
  ) => {
    const file = e.currentTarget.files?.[0];

    if (file) {
      await uploadFile(file);
    }
  };

  const handleDragAndUpload = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();

    const file = e.dataTransfer.files?.[0];
    console.log(file);

    if (file) {
      await uploadFile(file);
    }
  };

  return (
    <div>
      <div>
        <div>
          <label
            htmlFor="Upload_Invoice"
            className="text-[#344054] text-sm font-medium"
          >
            Upload Invoice
          </label>

          {getValues()?.invoice_document ? (
            <button
              type="button"
              className="text-sm flex items-center gap-1 text-custom-blue hover:underline"
              rel="noopener noreferrer" // Important for security
              onClick={() => {
                toggleDialog();
                getBlobFileData(getValues()?.invoice_document, jwtToken!);
              }}
            >
              <DocumentIcon className="size-[14px]" /> View your uploaded
              document
            </button>
          ) : null}
        </div>
        <DragAndDrop
          id="Upload_Invoice"
          onChange={(e) => {
            handleFileChangeAndUpload(e);

            toast.promise(handleFileChangeAndUpload(e), {
              pending: "File is uploading",
              success: "File has been uploaded 👌",
              error: "Rejected, please try again 🤯",
            });
          }}
          onDrop={(e) => {
            handleDragAndUpload(e);

            toast.promise(handleDragAndUpload(e), {
              pending: "File is uploading",
              success: "File has been uploaded 👌",
              error: "Rejected, please try again 🤯",
            });
          }}
        />
        <small className="text-red-500 h-[10px] block ml-1">
          {errors.invoice_document && errors.invoice_document?.message}
        </small>
      </div>

      {/* document dialog */}
      <Modal
        ref={viewDocumentDialogRef}
        toggleDialog={toggleDialog}
        className="max-w-[600px] w-full p-8 rounded-md backdrop:bg-black/50"
      >
        <h3 className="text-xl mb-4">Document</h3>
        <div className="flex justify-center">
          {documentFileData.fileUrl &&
            documentFileData.mimeType.startsWith("image/") && (
              <img id={`blob-data`} src={documentFileData.fileUrl} />
            )}
          {documentFileData.loading && (
            <SpinnerIcon className="size-8 text-gray-200 animate-spin fill-blue-600" />
          )}
          {documentFileData.error && (
            <p className="text-red-400">{documentFileData.error}</p>
          )}
        </div>

        {documentFileData.fileUrl &&
          documentFileData.mimeType === "application/pdf" && (
            <div className="h-[500px] flex justify-center">
              <iframe id={`blob-data`} src={documentFileData.fileUrl} />
            </div>
          )}
      </Modal>
    </div>
  );
}
